import {
  FOC_TOUR_LABEL_IAP_PREP_OPS,
  focFeedbackPopoverHtml,
} from '../focTourFeedback';
import {
  FOC_REQUIRED_DISPOSITION,
  IOC_CARRYOVER_DISPOSITION,
  focPlanningPRequirement,
  focPlanningPTourNavVisible,
  type FocPlanningPTourStepDef,
} from './focPlanningPTourTypes';
import { FOC_IAP_PREP_TOUR_HREF } from './focPlanningPTourRegistry';

export { FOC_IAP_PREP_TOUR_HREF, focPlanningPTourNavVisible };

/**
 * Unified 12.1–12.3 walk: IAP Prep & Approval (index 6) then Operations Brief (index 7).
 */
export const FOC_IAP_PREP_TOUR_STEPS: FocPlanningPTourStepDef[] = [
  {
    id: 'sectionIntro',
    phaseIndex: 6,
    title: '12.1–12.3 IAP Prep, Approval & Operations Brief',
    description:
      'IAP Prep & Approval coordinates the next-period package — component selection, form readiness, assembly, and approval. The Operations Brief is the IMH facilitation that briefs the approved plan before the next operational period starts.',
  },
  focPlanningPRequirement({
    id: 'iap001',
    requirementId: 'FOC-IAP-001',
    name: 'Configurable IAP Component Tracker',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'iap-components',
    widget: 'incidentActionPlan',
    description:
      'PRATUS provides an editable IAP component tracker for each operational period via the IAP cover attachment manifest. The tracker uses IMH-informed default components, required/not-required selection, and incident-specific appendables to identify the required IAP package. A separate tracker grid product is not required for this walkthrough.',
  }),
  focPlanningPRequirement({
    id: 'iap001a',
    requirementId: 'FOC-IAP-001a',
    name: 'Component Information',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-stepper',
    description:
      'Component name, product type, operational period, product/review/approval status, and form links are visible through the Planning-P stepper rail and form cards. Owners map to responsible roles on each form; release requirements, comments, actions, and dependencies are captured through Meeting Details, signatures, and ICS 233 open actions rather than tracker columns.',
  }),
  focPlanningPRequirement({
    id: 'iap001b',
    requirementId: 'FOC-IAP-001b',
    name: 'Supplemental Products',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'iap-components',
    widget: 'incidentActionPlan',
    description:
      'The IAP component selection supports standard ICS forms and incident-specific supplemental products — including plans, analyses, instructions, map/weather attachments, uploaded files, and other authorized documentation — via base forms, appendables, and file attachments on the cover/manifest.',
  }),
  focPlanningPRequirement({
    id: 'iap002',
    requirementId: 'FOC-IAP-002',
    name: 'IAP Assembly Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-form-card',
    widget: 'incidentActionPlan',
    description:
      'PRATUS provides an IAP assembly workspace that brings approved and required components into an ordered operational-period package through Generate & Preview Next IAP and the paginated IAP export.',
  }),
  focPlanningPRequirement({
    id: 'iap002a',
    requirementId: 'FOC-IAP-002a',
    name: 'IAP Quality Review',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'planning-stepper',
    description:
      'Before final IAP release, form status dots, version/signature indicators, and incomplete products on the Planning-P rail identify missing required work, outstanding approvals, and conditions that need attention. A dedicated package quality-checklist product is not required for this walkthrough.',
  }),
  focPlanningPRequirement({
    id: 'iap002b',
    requirementId: 'FOC-IAP-002b',
    name: 'Preview and Revision',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'iap-preview',
    widget: 'incidentActionPlan',
    description:
      'Authorized users can preview, review, revise source forms, and regenerate the assembled package before approval and release.',
  }),
  focPlanningPRequirement({
    id: 'iap002c',
    requirementId: 'FOC-IAP-002c',
    name: 'Approval and Release History',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'iap-cover',
    widget: 'incidentActionPlan',
    description:
      'PRATUS records IAP approval context through the IAP cover Incident Commander approval box, form version history, signatures, responsible users, and timestamps. Conditional approval, required changes, and release notes are captured via meeting outcomes and open actions. A separate package approval-history entity is not required for this walkthrough.',
  }),
  focPlanningPRequirement({
    id: 'iap003',
    requirementId: 'FOC-IAP-003',
    name: 'Source-Faithful Operational Products',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics-form-export',
    widget: 'incidentObjectives',
    description:
      'PRATUS creates, edits, approves, and exports Coast Guard ICS products in PDF/Word formats that preserve recognized official form structure and usable continuation behavior.',
  }),
  focPlanningPRequirement({
    id: 'er011',
    requirementId: 'US-ER011',
    name: 'Manage Organization Assignment List (ICS-203)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics203-card',
    widget: 'organizationAssignmentList',
    description:
      'As a user with permission, view and edit the ICS-203, defining personnel assigned to Command Staff, Agency Representatives, and Section Chiefs for the current operational period.',
  }),
  focPlanningPRequirement({
    id: 'er012',
    requirementId: 'US-ER012',
    name: 'Manage Assignment Lists (ICS-204)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics204-card',
    widget: 'assignmentList',
    description:
      'As a user with permission, view and edit ICS-204 forms for each Division or Group, ensuring resources, communications, and special instructions are documented for field supervisors.',
  }),
  focPlanningPRequirement({
    id: 'er013',
    requirementId: 'US-ER013',
    name: 'Manage Communications Plan (ICS-205)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics205-card',
    widget: 'incidentRadioCommunicationsPlan',
    description:
      'As a user with permission, view and edit the ICS-205, defining radio channels, frequencies, and communication methods for the incident.',
  }),
  focPlanningPRequirement({
    id: 'er014',
    requirementId: 'US-ER014',
    name: 'Manage Medical Plan (ICS-206)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics206-card',
    widget: 'medicalPlan',
    description:
      'As a user with permission, view and edit the ICS-206, documenting medical aid stations, transportation, and hospitals available for incident personnel.',
  }),
  focPlanningPRequirement({
    id: 'er010',
    requirementId: 'US-ER010',
    name: 'Manage Safety Plan (ICS-208)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics208-card',
    widget: 'safetyPlan',
    description:
      'As a user with permission, view and edit the ICS-208 so general safety messages and expanded safety plans stay current for the escalated operational period.',
  }),
  focPlanningPRequirement({
    id: 'er010hm',
    requirementId: 'US-ER010',
    name: 'Manage Site Safety and Control Plan (ICS-208HM)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics208hm-card',
    widget: 'hazardousMaterials',
    description:
      'ICS-208HM (Hazardous Materials / Site Safety and Control) is available alongside ICS-208 so hazardous-materials safety planning stays with the escalated operational period.',
  }),
  focPlanningPRequirement({
    id: 'er008',
    requirementId: 'US-ER008',
    name: 'Manage Incident Status Summary (ICS-209)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics209-card',
    widget: 'incidentStatusSummary',
    description:
      'As a user with permission, view and edit all fields within the ICS-209, including significant events, resource summaries, and cost estimates for external status reporting.',
  }),
  focPlanningPRequirement({
    id: 'er007',
    requirementId: 'US-ER007',
    name: 'Manage Open Action Tracker (ICS-233)',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics233-card',
    widget: 'openActionsTracker',
    description:
      'As a user with permission, view and edit all fields within the ICS-233, adding strategic issues, updating status, and assigning owners to long-term open actions.',
  }),
  focPlanningPRequirement({
    id: 'er015',
    requirementId: 'US-ER015',
    name: 'Export Escalated Response Forms',
    disposition: IOC_CARRYOVER_DISPOSITION,
    phaseIndex: 6,
    tour: 'ics-form-export',
    widget: 'openActionsTracker',
    description:
      'Export escalated response forms (including ICS-202, 203, 204, 205, 206, 208, 209, 215, 215A, 233, and 234) as PDF/Word so the physical IAP and related documentation can be distributed.',
  }),
  {
    id: 'sec123',
    phaseIndex: 7,
    title: '12.3 Operations Brief',
    description:
      'The Operations Brief is distinct from IAP Prep: it is the IMH-aligned facilitation where the approved plan is briefed to operations personnel before the next operational period.',
  },
  focPlanningPRequirement({
    id: 'iap004',
    requirementId: 'FOC-IAP-004',
    name: 'Operations Brief Presentation Workspace',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 7,
    tour: 'planning-agenda',
    showAgenda: true,
    description:
      'PRATUS provides an Operations Brief workspace based on the approved IAP. The IMH agenda supports distributed participation, presenter sequencing, operational product display, documentation of changes, and access to the plan. A separate slide-deck presentation builder is not required for this walkthrough.',
  }),
  focPlanningPRequirement({
    id: 'iap004a',
    requirementId: 'FOC-IAP-004a',
    name: 'Role-Specific Briefing Content',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 7,
    showAgenda: true,
    description:
      'The Operations Brief agenda supports role-specific briefing content including Command direction, objectives and priorities, situation status, operations and ICS 204 assignments, safety, communications, medical, logistics, finance/admin, partner/liaison considerations, contingency plans, and other incident-specific IAP components.',
  }),
  focPlanningPRequirement({
    id: 'iap004b',
    requirementId: 'FOC-IAP-004b',
    name: 'Approved Plan Access',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 7,
    tour: 'iap-components',
    widget: 'incidentActionPlan',
    description:
      'Users can access the applicable approved IAP components and preview the assembled plan directly from the Operations Brief workspace without searching unrelated menus or generic document repositories.',
  }),
  focPlanningPRequirement({
    id: 'iap004c',
    requirementId: 'FOC-IAP-004c',
    name: 'Briefing Updates',
    disposition: FOC_REQUIRED_DISPOSITION,
    phaseIndex: 7,
    tour: 'planning-meeting-details',
    openMeetingDetails: true,
    showAgenda: true,
    description:
      'Material adjustments, questions, decisions, and follow-up actions identified during the Operations Brief are recorded through Meeting Details, agenda notes, and ICS 233 open actions linked to the incident record and operational products.',
  }),
  {
    id: 'focFeedback',
    title: 'FOC Feedback Notes',
    description: focFeedbackPopoverHtml(FOC_TOUR_LABEL_IAP_PREP_OPS),
  },
];

export function focIapPrepTourStepIds(): string[] {
  return FOC_IAP_PREP_TOUR_STEPS.map((step) => step.id);
}
